<template>
  <div class="service-list">
    <div
      v-for="service in serviceItems"
      :key="service.category.id"
      class="service-list__wrapper"
    >
      <div class="service-list__title">
        <div>{{ service.category.name }}</div>

        <v-btn
          v-if="canEditFacilityServices"
          icon
          color="primary"
          @click="$emit('add-to-category', service.category)"
        >
          <v-icon>icon-edit</v-icon>
        </v-btn>
      </div>
      <transition-group
        name="blink"
        tag="div"
        class="service-list__content"
        enter-active-class="blink-enter-active"
        leave-active-class="blink-leave-active"
      >
        <GmbServicePreviewChip
          v-for="serviceItem in service.items"
          :key="serviceItem.id"
          :name="getServiceName(serviceItem)"
          :price="getPrice(serviceItem.price, serviceItem.currency_code)"
          @edit="handleEdit(serviceItem)"
          @delete="handleDelete(serviceItem)"
        />
      </transition-group>
    </div>
    <GmbServicesEditDialog
      v-if="selectedService"
      :selected-service="selectedService"
      :currency="currency"
      @update="$emit('update', $event)"
      @close="selectedService = null"
    />
  </div>
</template>

<script>
import GmbServicePreviewChip from "@/components/FacilityDetails/GmbServices/GmbServicePreviewChip.vue";
import GmbServicesEditDialog from "./GmbServicesEditDialog.vue";
import pickBy from "lodash/pickBy";
import { DialogHelper } from "@/helpers/dialog-helper";
import { SERVICE_ACTIONS_ENUM } from "@/enums/gmb-services.enum";
import gmbServicesMixin from "@/helpers/mixins/gmb-services";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

export default {
  name: "GmbServicePreview",
  mixins: [gmbServicesMixin, permissionsInfo],
  components: {
    GmbServicePreviewChip,
    GmbServicesEditDialog
  },
  props: {
    serviceItems: {
      type: Array,
      required: true
    },
    currency: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedService: null
    };
  },
  computed: {
    categories() {
      return this.serviceItems.map(service => {
        return service.category;
      });
    },
    services() {
      return this.serviceItems
        .filter(category => category.items)
        .map(service => {
          return service.items;
        })
        .flat();
    }
  },
  methods: {
    handleEdit(service) {
      this.selectedService = service;
    },
    handleDelete(service) {
      DialogHelper.showConfirmDialog(
        {
          text: this.$t(
            "message.facilityDetails.services.deleteServiceDialogText"
          ),
          okLabelKey: "message.common.yes",
          condensed: true
        },
        async () => {
          const deletedServiceItems = {
            service_items: [
              { action: SERVICE_ACTIONS_ENUM.DELETE, item: pickBy(service) }
            ]
          };
          this.detectChangesAndSendEvent(
            deletedServiceItems,
            this.serviceItems
          );
          await this.patchFacilityService(deletedServiceItems);
        }
      );
    },
    getServiceName(service) {
      return service.display_name || service.gmb_service_type.display_name;
    },
    getPrice(price, currency) {
      return price && currency ? `${price} ${currency}` : undefined;
    }
  }
};
</script>
<style lang="scss" scoped>
@keyframes scale-up {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes delete-chip-animation {
  0% {
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.9);
    color: $red-1;
    border-color: $red-1;
  }
}

::v-deep {
  .blink-enter-active {
    &.service-chip.v-chip {
      animation: scale-up 0.5s forwards;
    }
  }
  .blink-leave-active {
    &.service-chip.v-chip {
      animation: delete-chip-animation 0.5s forwards;
    }
  }
}
.service-list {
  ::v-deep span.service-chip.v-chip {
    @include vChip();
    white-space: pre-wrap;
  }
  &__title {
    font-size: 16px;
    margin-bottom: $spacing * 2;
    display: flex;
    align-items: center;
    gap: $spacing;
  }

  &__wrapper {
    border-bottom: 1px solid #e2e2e2;
    width: 100%;
    padding-bottom: $spacing * 2;

    & + & {
      padding-top: $spacing * 2;
    }
  }

  &__content {
    display: flex;
    gap: $spacing;
    flex-wrap: wrap;
  }
}
</style>
